import React, { startTransition, useTransition, useDeferredValue } from 'react';

/**
 * React 19 Performance Optimization Utilities
 * Provides modern performance patterns for React 19
 */

/**
 * Hook for managing expensive state updates with transitions
 * @returns Transition state and function
 */
export function useExpensiveUpdate() {
  return useTransition();
}

/**
 * Hook for deferring expensive values
 * @param value - Value to defer
 * @returns Deferred value
 */
export function useExpensiveValue<T>(value: T): T {
  return useDeferredValue(value);
}

/**
 * Wrapper for expensive calculations that should be transitioned
 * @param callback - Expensive calculation function
 * @returns Promise that resolves when transition is complete
 */
export function withTransition<T>(callback: () => T): Promise<T> {
  return new Promise((resolve) => {
    startTransition(() => {
      const result = callback();
      resolve(result);
    });
  });
}

/**
 * Debounced state update with transition
 * @param setState - State setter function
 * @param value - New state value
 * @param delay - Debounce delay in milliseconds
 */
export function debouncedTransition<T>(
  setState: (value: T) => void,
  value: T,
  delay: number = 100
): () => void {
  const timeoutId = setTimeout(() => {
    startTransition(() => {
      setState(value);
    });
  }, delay);

  return () => clearTimeout(timeoutId);
}

/**
 * Performance monitoring hook for React 19
 * @param name - Name for performance tracking
 * @returns Performance monitoring functions
 */
export function usePerformanceMonitor(name: string) {
  const startTime = performance.now();
  
  const endMonitoring = () => {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Only warn in development, not in tests
    if (duration > 16 && process.env.NODE_ENV !== 'test') {
      console.warn(`Performance warning: ${name} took ${duration.toFixed(2)}ms`);
    }
    
    return duration;
  };
  
  return {
    endMonitoring,
    startTime,
  };
}

/**
 * Enhanced performance monitoring with detailed metrics
 */
export function usePerformanceMonitoring(componentName: string = 'Unknown') {
  const [renderCount, setRenderCount] = React.useState(0);
  const [averageRenderTime, setAverageRenderTime] = React.useState(0);

  const startMonitoring = React.useCallback((name: string = componentName) => {
    const startTime = performance.now();
    const initialMemory = (performance as any).memory?.usedJSHeapSize || 0;

    const endMonitoring = () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      const currentMemory = (performance as any).memory?.usedJSHeapSize || 0;
      const memoryDelta = currentMemory - initialMemory;

      // Update render count and average
      setRenderCount(prev => prev + 1);
      setAverageRenderTime(prev => {
        const newAverage = (prev * (renderCount) + duration) / (renderCount + 1);
        return newAverage;
      });

      // Performance warnings
      if (duration > 32) {
        console.error(`[Performance Error] ${name} took ${duration.toFixed(2)}ms`);
      } else if (duration > 16 && process.env.NODE_ENV !== 'test') {
        console.warn(`[Performance Warning] ${name} took ${duration.toFixed(2)}ms`);
      }

      // Memory leak detection
      if (memoryDelta > 1000000) { // 1MB threshold
        console.warn(`[Memory Warning] ${name} used ${(memoryDelta / 1000000).toFixed(2)}MB`);
      }

      return duration;
    };

    return endMonitoring;
  }, [componentName, renderCount]);

  return { 
    startMonitoring, 
    renderCount, 
    averageRenderTime,
  };
}

/**
 * Hook for managing large list rendering with virtualization
 * @param items - Array of items to render
 * @param pageSize - Number of items per page
 * @returns Virtualized list state
 */
export function useVirtualizedList<T>(
  items: T[],
  pageSize: number = 50
) {
  const [currentPage, setCurrentPage] = React.useState(0);
  const deferredPage = useExpensiveValue(currentPage);
  
  const startPageTransition = (newPage: number) => {
    setCurrentPage(newPage);
  };
  
  const startIndex = deferredPage * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleItems = items.slice(startIndex, endIndex);
  
  return {
    visibleItems,
    currentPage: deferredPage,
    totalPages: Math.ceil(items.length / pageSize),
    startPageTransition,
    hasMore: endIndex < items.length,
    hasPrevious: startIndex > 0,
  };
}

/**
 * Hook for managing expensive form validation
 * @param validationFn - Validation function
 * @param value - Value to validate
 * @returns Validation state
 */
export function useExpensiveValidation<T>(
  validationFn: (value: T) => Promise<{ isValid: boolean; errors: string[] }>,
  value: T
) {
  const [validationResult, setValidationResult] = React.useState<{ isValid: boolean; errors: string[] } | null>(null);
  const deferredResult = useExpensiveValue(validationResult);
  
  const validate = () => {
    validationFn(value).then(setValidationResult);
  };
  
  return {
    validationResult: deferredResult,
    validate,
  };
}
